import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import HelpPopover from '../help/HelpPopover'

/**
 * The Invention intake card (figure S3) — the inventor's own declaration of
 * what the invention is, the problem it solves, and how.
 *
 * It is the one part of the invention page NOT written by the analysis, which
 * is why it sits first: everything below it is a machine's reading of what this
 * card asserts. All three answers are stored on the project (the problem and
 * approach fields were added with this screen; the declaration already existed)
 * and are encrypted like the rest of the inventor's prose.
 *
 * The edit form is the only way in. The page-level declaration prompt, editor
 * and green "Invention Declaration" strip the old shell stacked above the tabs
 * are gone — the figure shows none of them — so the capability lives here, on
 * the card that shows the answers.
 */
export interface InventionIntake {
  whatItIs?: string
  problem?: string
  approach?: string
}

export interface InventionIntakeCardProps {
  intake: InventionIntake
  /** Topics read from this invention's analysis. Never invented here. */
  topics?: string[]
  canEdit?: boolean
  /** The figure draws NO control on this card, so the page opens the editor —
   * on S3 that is the header's ⋯ More menu. Left undefined the card keeps its
   * own state, so it can still be exercised on its own. */
  editing?: boolean
  onEditingChange?: (editing: boolean) => void
  /** `whatItIs` is absent from `next` when the declaration is locked, so a save
   * can never write over a declaration this account cannot read. */
  onSave?: (next: Omit<Required<InventionIntake>, 'whatItIs'> & { whatItIs?: string }) => Promise<void>
  /** UX-022: the declaration exists but cannot be decrypted for this account.
   * The card shows it as Locked, hides the "add it" hint, disables its field
   * and keeps it out of every save. */
  declarationUnreadable?: boolean
}

const LABELS: [keyof InventionIntake, string, string][] = [
  ['whatItIs', 'What it is', 'One sentence: what the invention is.'],
  ['problem', 'Problem', 'What is wrong today, and for whom.'],
  ['approach', 'Approach', 'How your invention solves it.'],
]

export default function InventionIntakeCard({
  intake, topics, canEdit, editing: editingProp, onEditingChange, onSave, declarationUnreadable = false,
}: InventionIntakeCardProps) {
  const [selfEditing, setSelfEditing] = useState(false)
  const editing = editingProp ?? selfEditing
  const setEditing = (next: boolean) => {
    if (onEditingChange) onEditingChange(next)
    else setSelfEditing(next)
  }
  const [draft, setDraft] = useState<Required<InventionIntake>>({
    whatItIs: intake.whatItIs ?? '',
    problem: intake.problem ?? '',
    approach: intake.approach ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const declared = !!intake.whatItIs?.trim()
  const locked = declarationUnreadable

  // Opening the editor seeds it from the record, wherever the open came from —
  // the page's ⋯ More menu now, this card's own state in a standalone mount.
  useEffect(() => {
    if (!editing) return
    setDraft({
      whatItIs: intake.whatItIs ?? '',
      problem: intake.problem ?? '',
      approach: intake.approach ?? '',
    })
    setError('')
  }, [editing, intake.whatItIs, intake.problem, intake.approach])

  const save = async () => {
    if (!onSave) return
    setSaving(true)
    setError('')
    try {
      // A locked declaration is never part of the payload: its draft is just ''
      // and sending it would overwrite the real, unreadable value.
      if (locked) {
        const { whatItIs: _unreadable, ...rest } = draft
        await onSave(rest)
      } else {
        await onSave(draft)
      }
      setEditing(false)
    } catch {
      // Never surface the raw transport error (e.g. "Request failed with
      // status code 500") — F9: it read as an unhandled crash, not a
      // recoverable save failure, and it overlapped the Save/Cancel controls.
      setError('That did not save. Try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section data-part="InventionIntakeCard" className="light-card p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 text-[13px] font-bold text-iflow-900">
          🧾 Invention intake
          <HelpPopover
            topic="concept-invention-intake"
            title="Invention intake"
            body="Your invention declaration — what it is, the problem it solves and how. Edit it any time from ⋯ More; matching reads topic overlap only."
            size={14}
          />
        </h3>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
              declared && !locked ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'
            }`}
          >
            {locked ? '🔒 Locked' : declared ? '✓ Declared' : 'Not declared yet'}
          </span>
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          {LABELS.map(([field, label, hint]) => (
            <label key={field} className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{label}</span>
              <textarea
                value={locked && field === 'whatItIs' ? '' : draft[field]}
                onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
                rows={2}
                placeholder={locked && field === 'whatItIs' ? 'Locked: can’t be read with your current key' : hint}
                aria-label={label}
                disabled={locked && field === 'whatItIs'}
                className="mt-1 w-full disabled:cursor-not-allowed disabled:bg-gray-100 rounded-lg border border-gray-300 px-3 py-2 text-[12.5px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-iflow-500"
              />
            </label>
          ))}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving || (!locked && !draft.whatItIs.trim())}
              className="rounded-lg bg-iflow-700 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-iflow-800 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-[12.5px] text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
          {error && <p className="text-[11.5px] font-medium text-red-600">{error}</p>}
        </div>
      ) : (
        <dl>
          {LABELS.map(([field, label]) => (
            <div key={field}>
              <dt className="mt-2.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 first:mt-0">
                {label}
              </dt>
              <dd className="mt-0.5 text-[12.5px] leading-relaxed text-gray-700">
                {locked && field === 'whatItIs' ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                    <p className="font-semibold text-amber-800">🔒 Locked: can’t be read with your current key</p>
                    <p data-testid="declaration-locked-reason" className="mt-1 text-[12.5px] text-amber-900">
                      This declaration is encrypted and your account’s key can’t open it, so it isn’t shown here. It
                      has not been deleted. Check your key access, or ask your organisation admin to.
                    </p>
                    <Link to="/account/security" className="mt-1 inline-block text-[12.5px] font-semibold text-iflow-700 underline hover:text-iflow-900">
                      Check your key access
                    </Link>
                  </div>
                ) : intake[field]?.trim() ? (
                  intake[field]
                ) : (
                  <span className="text-gray-500">
                    Not recorded yet{canEdit ? ' — add it from ⋯ More.' : '.'}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {topics && topics.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {topics.map((topic) => (
            <span
              key={topic}
              className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-[11px] text-iflow-800"
            >
              {topic}
            </span>
          ))}
        </div>
      )}
    </section>
  )
}
